import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { asDragSource } from "../../components/dragAndDrop/DraggableComponent";
import Icon from "../../components/interface/Icon";
import Identifier from "../../components/interface/Identifier";
import ItemCard from "../../components/itemCardView/ItemCardView";
import { withScreenContext } from "../../components/screen/ScreenContext";
import compactDisplays from "../../constants/compactDisplay";
import { MINDMAP_VIEW } from "../../constants/identifiers";
import { PropTypeShapes } from "../../types";
import { wrapEditingComponent } from "../../utils/codeSplit";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";

// import debugUtils from "../../utils/debugUtils";

const dragCallbacks = {
  beginDrag: _props => {
    // TODO: Do we want to set isDragging or similar...?
  },
  canDrag: (props, dragSource, e) => {
    return dragSource.component.canDrag(e);
  },

  getDragSourceParams: props => {
    return {
      itemId: props.itemId,
      itemType: props.typeData.id,
      sourceView: props.viewId,
      sourceViewType: MINDMAP_VIEW
    };
  },

  getPreviewElement: props => renderNodePreview(props)
};

const renderNodePreview = nodeProps => {
  const cloneProps = _.clone(nodeProps);
  cloneProps.selected = false;
  cloneProps.relativeLeft = 0;
  cloneProps.relativeTop = 0;
  cloneProps.onClick = null;
  cloneProps.onSuperClick = null;
  cloneProps.autoEditWhenBlank = false;
  return renderNode(cloneProps, false);
};

const renderConnector = (useProps, onConnectorPointerDown) =>
  !useProps.screenContext || useProps.screenContext.isEditMode ? (
    <div
      className="mindmapNodeAddConnection"
      onMouseDown={onConnectorPointerDown}
      onTouchStart={onConnectorPointerDown}
    >
      <Icon icon="mindmapConnector" color={useProps.draggingConnector ? "iconActive" : "iconMono"} />
    </div>
  ) : null;

const renderNode = (
  useProps,
  showConnector,
  onChangeEditing,
  onItemClick,
  onSuperClick,
  onLabelChange,
  onConnectorPointerDown,
  onItemDoubleClick
) => {
  let classes = ["mindmapNode", useProps.color];
  if (useProps.selected) {
    classes.push("selected");
  }
  if (useProps.draggingConnector) {
    classes.push("draggingConnector");
  }

  const opacity = useProps.isDragging ? 0 : useProps.isFiltered ? 0.4 : 1;

  const nodeStyle = {
    left: `${useProps.relativeLeft}px`,
    top: `${useProps.relativeTop}px`,
    width: `${useProps.width}px`,
    height: `${useProps.height}px`,
    opacity
  };

  const isAvatarType = useProps.typeData.compactDisplay === compactDisplays.AVATAR_INITIALS;

  return (
    <div id={`mindmapNode-${useProps.nodeId}`} className={classes.join(" ")} style={nodeStyle}>
      <div
        className={`mindmapNodeSuperBar ${useProps.superColor ? useProps.superColor : ""}`}
        title={useProps.superLabel ? `Parent: ${useProps.superLabel}` : null}
        onClick={onSuperClick}
      />
      <div className="mindmapNodeContentWrap" onClick={onItemClick} onDoubleClick={onItemDoubleClick}>
        <Icon icon={useProps.typeData.icon} color={useProps.typeData.color} />
        <ItemCard
          {...useProps.mindmapViewSettings.toObject()}
          showIdentifier={false}
          label={useProps.label}
          titleMetrics={useProps.titleMetrics}
          dateTextSize={useProps.dateTextSize}
          narrativeTextSize={useProps.narrativeTextSize}
          durationTextSize={useProps.durationTextSize}
          summaryMetrics={useProps.summaryMetrics}
          identifierSize={useProps.identifierSize}
          identifier={useProps.displayId}
          relatedItemsByType={useProps.relatedItemsByType}
          blockingIdentifiers={useProps.blockingIdentifiers}
          blockedByIdentifiers={useProps.blockedByIdentifiers}
          imagePath={useProps.imagePath}
          isCompleted={useProps.isCompleted}
          onLabelChange={onLabelChange}
          onChangeEditing={onChangeEditing}
          autoEditWhenBlank={!useProps.isDragging ? useProps.autoEditWhenBlank : false}
          isSelected={useProps.selected}
          isActiveView={useProps.isActiveView}
          typesById={useProps.typesById}
          type={useProps.typeData.id}
        />
        {useProps.mindmapViewSettings.showIdentifier ? (
          <Identifier
            isAvatar={isAvatarType}
            label={isAvatarType ? useProps.initials : useProps.displayId}
            color={useProps.color}
          />
        ) : null}
      </div>
      {wrapEditingComponent(
        showConnector && !useProps.isDragging ? renderConnector(useProps, onConnectorPointerDown) : null
      )}
    </div>
  );
};

class MindmapNodeView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.onChangeEditing = this.onChangeEditing.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onItemDoubleClick = this.onItemDoubleClick.bind(this);
    this.onSuperClick = this.onSuperClick.bind(this);
    this.onLabelChange = this.onLabelChange.bind(this);
    this.onConnectorPointerDown = this.onConnectorPointerDown.bind(this);

    this._isEditing = false;
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("MindmapNodeView", this.props, nextProps, true);
  // }

  canDrag(e) {
    // Disable the drag and drop when editing inside the node
    // The drag and drop may impact on the click event and focusing.4
    return !this._isEditing && this.props.allowDrag && !hasAncestorOfClass(e.target, "mindmapNodeAddConnection");
  }

  onChangeEditing(value) {
    this._isEditing = value;
  }

  onItemClick(e) {
    this.props.onClick ? this.props.onClick(this.props.itemId, getModifierKeysForEvent(e), this.props.selected) : null;
  }

  onItemDoubleClick() {
    this.props.onDoubleClick ? this.props.onDoubleClick() : null;
  }

  onSuperClick(e) {
    if (this.props.super) {
      this.props.onClick ? this.props.onClick(this.props.super, {}, false) : null;
    } else {
      this.onItemClick(e);
    }
  }

  onLabelChange(label) {
    this.props.onLabelChange ? this.props.onLabelChange([this.props.id], label) : null;
  }

  onConnectorPointerDown(e) {
    this.props.onConnectorPointerDown(this.props.id, e);
  }

  render() {
    let showConnector = !this.props.draggingConnector;
    //#!if iOS === true
// showConnector = true;
    //#!endif
    return renderNode(
      this.props,
      showConnector,
      this.onChangeEditing,
      this.onItemClick,
      this.onSuperClick,
      this.onLabelChange,
      this.onConnectorPointerDown,
      this.onItemDoubleClick
    );
  }
}

MindmapNodeView.propTypes = {
  viewId: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onConnectorPointerDown: PropTypes.func,
  onLabelChange: PropTypes.func,
  nodeId: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  label: PropTypes.string,
  isActiveView: PropTypes.bool,
  selected: PropTypes.bool.isRequired,
  isFiltered: PropTypes.bool.isRequired,
  color: PropTypes.string.isRequired,
  relativeLeft: PropTypes.number.isRequired,
  relativeTop: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  mindmapViewSettings: PropTypeShapes.mindmapViewSettings.isRequired,
  typeData: PropTypeShapes.itemType.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,

  // Should the node automatically edit?
  autoEditWhenBlank: PropTypes.bool.isRequired,

  superColor: PropTypes.string,
  superLabel: PropTypes.string,
  super: PropTypes.string,

  draggingConnector: PropTypes.bool,

  initials: PropTypes.string,
  displayId: PropTypes.string,
  titleMetrics: PropTypeShapes.textMetrics.isRequired,
  dateTextSize: PropTypeShapes.textSize.isRequired,
  durationTextSize: PropTypeShapes.textSize.isRequired,
  narrativeTextSize: PropTypeShapes.textSize.isRequired,
  summaryMetrics: PropTypeShapes.textMetrics.isRequired,
  identifierSize: PropTypeShapes.size.isRequired,
  relatedItemsByType: ImmutablePropTypes.list.isRequired,
  blockingIdentifiers: PropTypeShapes.identifierList.isRequired,
  blockedByIdentifiers: PropTypeShapes.identifierList.isRequired,
  imagePath: PropTypes.string,
  isCompleted: PropTypes.bool,

  allowDrag: PropTypes.bool.isRequired,

  // From asDragSource
  isDragging: PropTypes.bool,
  dragIsFromSameSource: PropTypes.bool,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

let exportedView = asDragSource(MindmapNodeView, MINDMAP_VIEW, dragCallbacks);
//#!if iOS === true
// exportedView = withScreenContext(exportedView);
//#!endif
export default exportedView;
